/**
 * Pure review-mode gate — kept in its own module (NO `@/review/*` imports)
 * so `main.tsx` can decide whether to mount the review layer WITHOUT
 * statically pulling in the review subsystem.
 *
 * The review layer's source lives under `apps/ordo/tools/app-explorer/`
 * (aliased via `@/review/*` in `vite.config.ts`). That `tools/` tree is a
 * developer-only surface excluded from the prod Docker image (root
 * `.dockerignore` + a hard guard in `apps/ordo/Dockerfile` stage 2). So
 * anything that reaches a `@/review/*` alias MUST be dynamically imported
 * behind a statically-false `import.meta.env.DEV` branch — otherwise
 * rolldown tries to resolve those files at build time and the prod
 * `build:client` step fails (the files are absent in the image).
 *
 * `isReviewMode()` is `import.meta.env.DEV`-gated, so in a prod build it
 * folds to a constant `false`. `main.tsx` then never awaits the dynamic
 * `import('./review-mount')`, and rolldown dead-code-eliminates that whole
 * branch — including the `@/review/*` chain — before alias resolution.
 */

const REVIEW_FLAG_KEY = 'ordo:review-mode'

/**
 * Check at boot whether to mount the review layer. Sticky: once turned on
 * via `?review=1`, persists until `?review=0` clears it. Returns `false`
 * in production builds (the `import.meta.env.DEV` guard folds to a
 * constant), which is what lets the review subsystem tree-shake away.
 */
export function isReviewMode(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  if (!import.meta.env.DEV) {
    return false
  }
  const params = new URLSearchParams(window.location.search)
  const fromUrl = params.get('review')
  if (fromUrl === '1') {
    window.localStorage.setItem(REVIEW_FLAG_KEY, '1')
    return true
  }
  if (fromUrl === '0') {
    window.localStorage.removeItem(REVIEW_FLAG_KEY)
    return false
  }
  return window.localStorage.getItem(REVIEW_FLAG_KEY) === '1'
}
